// src/pages/health/HealthExportPage.tsx
// Health data export page with format selection and progress

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Download,
  ArrowLeft,
  FileSpreadsheet,
  FileJson,
  FileText,
  File,
  CheckCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { ExportWizard } from '@/features/health/components/export/ExportWizard';
import type { ExportFormat, ExportConfiguration } from '@/features/health/types/healthTypes/reportTypes';
import { format } from 'date-fns';

interface ExportHistory {
  id: string;
  format: ExportFormat;
  dateRange: { startDate: string; endDate: string };
  fileName: string;
  fileSize: string;
  exportedAt: string;
  status: 'completed' | 'failed';
}

// Mock export history
const mockExportHistory: ExportHistory[] = [
  {
    id: '1',
    format: 'csv',
    dateRange: { startDate: '2024-12-01', endDate: '2024-12-31' },
    fileName: 'health-data-dec-2024.csv',
    fileSize: '124 KB',
    exportedAt: '2024-12-31T15:30:00Z',
    status: 'completed',
  },
  {
    id: '2',
    format: 'pdf',
    dateRange: { startDate: '2024-10-01', endDate: '2024-12-31' },
    fileName: 'health-report-q4-2024.pdf',
    fileSize: '2.1 MB',
    exportedAt: '2024-12-30T10:00:00Z',
    status: 'completed',
  },
];

const formatIcons: Record<ExportFormat, React.ElementType> = {
  csv: FileSpreadsheet,
  json: FileJson,
  pdf: FileText,
  excel: File,
};

const formatColors: Record<ExportFormat, string> = {
  csv: 'text-green-600 bg-green-50',
  json: 'text-yellow-600 bg-yellow-50',
  pdf: 'text-red-500 bg-red-50',
  excel: 'text-emerald-600 bg-emerald-50',
};

export const HealthExportPage: React.FC = () => {
  const { familyId, childId } = useParams<{ familyId: string; childId: string }>();
  const navigate = useNavigate();
  
  const [showWizard, setShowWizard] = useState(false);
  const [exportHistory] = useState<ExportHistory[]>(mockExportHistory);

  const handleExportComplete = (config: ExportConfiguration) => {
    console.log('Export completed with config:', config);
    setShowWizard(false);
    // In real implementation, this would trigger the export and update history
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-white border-b border-warm-gray/10 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-cream rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-navy" />
              </button>
              <div className="p-2 bg-warm-gray/10 rounded-xl">
                <Download className="w-6 h-6 text-warm-gray" />
              </div>
              <div>
                <h1 className="text-xl font-display font-semibold text-navy">
                  Export Health Data
                </h1>
                <p className="text-sm text-warm-gray">
                  Download your health records in various formats
                </p>
              </div>
            </div>

            {!showWizard && (
              <button
                onClick={() => setShowWizard(true)}
                className="flex items-center gap-2 px-4 py-2 bg-sage text-white
                         rounded-xl hover:bg-sage-dark transition-colors"
              >
                <Download className="w-4 h-4" />
                New Export
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {showWizard ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <ExportWizard
              childId={childId || ''}
              onComplete={handleExportComplete}
              onCancel={() => setShowWizard(false)}
            />
          </motion.div>
        ) : (
          <>
            {/* Quick Export Options */}
            <section className="mb-8">
              <h2 className="text-lg font-display font-semibold text-navy mb-4">
                Quick Export
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {(['csv', 'json', 'pdf', 'excel'] as ExportFormat[]).map((format) => {
                  const Icon = formatIcons[format];
                  return (
                    <button
                      key={format}
                      onClick={() => setShowWizard(true)}
                      className="p-6 bg-white rounded-2xl border border-warm-gray/10
                               hover:shadow-soft transition-all text-left group"
                    >
                      <div className={`p-3 rounded-xl w-fit ${formatColors[format]}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <h3 className="font-medium text-navy mt-4 uppercase">
                        {format}
                      </h3>
                      <p className="text-sm text-warm-gray mt-1">
                        {format === 'csv' && 'Spreadsheet compatible'}
                        {format === 'json' && 'Developer friendly'}
                        {format === 'pdf' && 'Print ready report'}
                        {format === 'excel' && 'Microsoft Excel'}
                      </p>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Export History */}
            <section>
              <h2 className="text-lg font-display font-semibold text-navy mb-4">
                Export History
              </h2>
              
              {exportHistory.length === 0 ? (
                <div className="bg-white rounded-2xl border border-warm-gray/10 p-8 text-center">
                  <Download className="w-12 h-12 text-warm-gray/30 mx-auto mb-4" />
                  <p className="text-warm-gray">No exports yet</p>
                  <p className="text-sm text-warm-gray/70 mt-1">
                    Your export history will appear here
                  </p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-warm-gray/10 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-warm-gray/10">
                        <th className="text-left px-6 py-4 text-sm font-medium text-warm-gray">
                          File
                        </th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-warm-gray">
                          Date Range
                        </th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-warm-gray">
                          Size
                        </th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-warm-gray">
                          Exported
                        </th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-warm-gray">
                          Status
                        </th>
                        <th className="px-6 py-4"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {exportHistory.map((item) => {
                        const Icon = formatIcons[item.format];
                        return (
                          <tr
                            key={item.id}
                            className="border-b border-warm-gray/5 last:border-0"
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${formatColors[item.format]}`}>
                                  <Icon className="w-4 h-4" />
                                </div>
                                <span className="font-medium text-navy">
                                  {item.fileName}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-warm-gray text-sm">
                              {format(new Date(item.dateRange.startDate), 'MMM d')} -{' '}
                              {format(new Date(item.dateRange.endDate), 'MMM d, yyyy')}
                            </td>
                            <td className="px-6 py-4 text-warm-gray text-sm">
                              {item.fileSize}
                            </td>
                            <td className="px-6 py-4 text-warm-gray text-sm">
                              {format(new Date(item.exportedAt), 'MMM d, yyyy h:mm a')}
                            </td>
                            <td className="px-6 py-4">
                              {item.status === 'completed' ? (
                                <span className="flex items-center gap-1 text-sm text-green-600">
                                  <CheckCircle className="w-4 h-4" />
                                  Completed
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-sm text-red-500">
                                  <AlertCircle className="w-4 h-4" />
                                  Failed
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <button
                                className="p-2 hover:bg-cream rounded-lg transition-colors"
                                title="Download again"
                              >
                                <Download className="w-4 h-4 text-warm-gray" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Export Info */}
            <section className="mt-8 p-6 bg-white rounded-2xl border border-warm-gray/10">
              <h3 className="font-display font-semibold text-navy mb-4">
                About Data Exports
              </h3>
              <div className="grid md:grid-cols-2 gap-6 text-sm text-warm-gray">
                <div>
                  <h4 className="font-medium text-navy mb-2">What's Included</h4>
                  <ul className="space-y-1">
                    <li>• All health log entries (seizures, medications, etc.)</li>
                    <li>• Medication schedules and tracking</li>
                    <li>• Symptom records with severity</li>
                    <li>• Therapy session notes</li>
                    <li>• Vital signs and measurements</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-navy mb-2">Format Guide</h4>
                  <ul className="space-y-1">
                    <li><strong>CSV:</strong> Best for spreadsheets and analysis</li>
                    <li><strong>JSON:</strong> Best for developers and integrations</li>
                    <li><strong>PDF:</strong> Best for sharing with providers</li>
                    <li><strong>Excel:</strong> Best for detailed data manipulation</li>
                  </ul>
                </div>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default HealthExportPage;

